/**
 * Prevents firing multiple times when multiple prefixed events are applied.
 * i.e. el.addEventListener('webkitTransitionEnd transitionend')
 *
 * Usage:
 * const TRANSITIONEND = whichTransitionEvent();
 */

// why moudule, es gibt doch ein package TODO
const whichTransitionEvent = () => {
  if (typeof document === 'undefined') {
    return;
  }
  const el = document.createElement('fakeelement');
  const transitions = {
    transition: 'transitionend',
    OTransition: 'oTransitionEnd',
    MozTransition: 'transitionend',
    WebkitTransition: 'webkitTransitionEnd',
  };

  for (const t in transitions) {
    if (el.style[t] !== undefined) {
      return transitions[t];
    }
  }
};

const transitionEndEvent = whichTransitionEvent();

const EASINGS = {
  // Cubic
  easeInCubic: 'cubic-bezier(0.550, 0.055, 0.675, 0.190)',
  easeOutCubic: 'cubic-bezier(0.215, 0.610, 0.355, 1.000)',
  easeInOutCubic: 'cubic-bezier(0.645, 0.045, 0.355, 1.000)',

  // Circ
  easeInCirc: 'cubic-bezier(0.600, 0.040, 0.980, 0.335)',
  easeOutCirc: 'cubic-bezier(0.075, 0.820, 0.165, 1.000)',
  easeInOutCirc: 'cubic-bezier(0.785, 0.135, 0.150, 0.860)',

  // Expo
  easeInExpo: 'cubic-bezier(0.950, 0.050, 0.795, 0.035)',
  easeOutExpo: 'cubic-bezier(0.190, 1.000, 0.220, 1.000)',
  easeInOutExpo: 'cubic-bezier(1.000, 0.000, 0.000, 1.000)',

  // Quad
  easeInQuad: 'cubic-bezier(0.550, 0.085, 0.680, 0.530)',
  easeOutQuad: 'cubic-bezier(0.250, 0.460, 0.450, 0.940)',
  easeInOutQuad: 'cubic-bezier(0.455, 0.030, 0.515, 0.955)',

  // Quart
  easeInQuart: 'cubic-bezier(0.895, 0.030, 0.685, 0.220)',
  easeOutQuart: 'cubic-bezier(0.165, 0.840, 0.440, 1.000)',
  easeInOutQuart: 'cubic-bezier(0.770, 0.000, 0.175, 1.000)',

  // Quint
  easeInQuint: 'cubic-bezier(0.755, 0.050, 0.855, 0.060)',
  easeOutQuint: 'cubic-bezier(0.230, 1.000, 0.320, 1.000)',
  easeInOutQuint: 'cubic-bezier(0.860, 0.000, 0.070, 1.000)',

  // Sine
  easeInSine: 'cubic-bezier(0.470, 0.000, 0.745, 0.715)',
  easeOutSine: 'cubic-bezier(0.390, 0.575, 0.565, 1.000)',
  easeInOutSine: 'cubic-bezier(0.445, 0.050, 0.550, 0.950)',

  // Back
  easeInBack: 'cubic-bezier(0.600, -0.280, 0.735, 0.045)',
  easeOutBack: 'cubic-bezier(0.175,  0.885, 0.320, 1.275)',
  easeInOutBack: 'cubic-bezier(0.680, -0.550, 0.265, 1.550)',
};

/**
 * @typedef {Object} ShowOptions
 * @property {HTMLElement} target - The DOM element to show/hide.
 * @property {HTMLElement} handler - The DOM element that triggers the show/hide action.
 * @property {string} [easing='easeInOutQuart'] - The easing function to use for the animation.
 * @property {number} [duration=400] - The duration of the animation in milliseconds.
 * @property {boolean} [initOpened=false] - Whether the target should be initially opened.
 * @property {string} [openClassName='is-open'] - The class name to add to the target when it's opened.
 * @property {Show[]} [closeInstances=[]] - An array of Show instances that should be closed if one opens.
 * @property {function} [toggleStart] - A callback function that is called after the animation started. It receives the element, a string ('show') and the state as boolean.
 * @property {function} [toggleEnd] - A callback function that is called after the animation ended. It receives the element, a string ('hide') and the state as boolean.
 */

class Accordion {
  constructor(options) {
    this.target = options.target;
    this.targetInner = options.targetInner;
    this.handler = options.handler;
    this.openClassName = options.openClassName || 'is-open';
    this.initOpened = options.initOpened;
    this.show = this.initOpened || false;
    this.duration = options.duration || 400;
    this.easing = EASINGS[options.easing] || options.easing || 'ease-out';
    this.toggleStart =
      typeof options.toggleStart === 'function' ? options.toggleStart : null;
    this.toggleEnd =
      typeof options.toggleEnd === 'function' ? options.toggleEnd : null;
    this._closeInstances = [];
    this.isAnimating = false;
    this.preventAllClose = options.preventAllClose || false;
    this.toggleAccordion = this.toggle.bind(this);
    this.transitionEndShower = this.onTransitionEnd.bind(this);
    this.uniqueStr = `${Math.floor(
      Math.random() * Math.floor(Math.random() * Date.now())
    )}`;
  }

  set closeInstances(instances) {
    this._closeInstances = instances;
  }

  get closeInstances() {
    return this._closeInstances;
  }

  onTransitionEnd() {
    if (this.show) {
      this.target.setAttribute('aria-hidden', 'false');
      this.toggleEnd && this.toggleEnd(this.target, 'show', this.show);
    } else {
      this.target.setAttribute('aria-hidden', 'true');
      this.toggleEnd && this.toggleEnd(this.target, 'hide', this.show);
      this.targetInner.style.display = 'none';
    }
    this.isAnimating = false;
  }

  setProps = () => {
    if (!this.show) {
      this.handler.setAttribute('aria-expanded', 'false');
      this.target.style.gridTemplateRows = '0fr';
      if (this.openClassName) {
        this.target.classList.remove(this.openClassName);
        this.handler.classList.remove(this.openClassName);
      }

      return;
    }

    this.handler.setAttribute('aria-expanded', 'true');
    this.target.style.gridTemplateRows = '1fr';
    if (this.openClassName) {
      this.target.classList.add(this.openClassName);
      this.handler.classList.add(this.openClassName);
    }
  };

  setInitialProps = () => {
    this.target.style.display = 'grid';
    this.target.style.transitionProperty = 'grid-template-rows';
    this.target.style.transitionDuration = `${this.duration}ms`;
    this.target.style.transitionTimingFunction = this.easing;
    this.targetInner.style.overflow = 'hidden';
    if (!this.initOpened) this.targetInner.style.display = 'none';
    this.target.setAttribute('id', `accordion-${this.uniqueStr}`);
    this.handler.setAttribute('aria-controls', `accordion-${this.uniqueStr}`);
    this.target.setAttribute('aria-hidden', `${!this.show}`);
  };

  preventLastClose = () => {
    // prevent last open accordion from closing
    const status = this.handler.getAttribute('aria-expanded');

    return status === 'true';
  };

  setOpenParameters = () => {
    this.show = true;
    this.setProps();

    this.toggleStart && this.toggleStart(this.target, 'show', this.show);
  };

  open = () => {
    if (this.isAnimating) return;
    if (!this.show) this.isAnimating = true;

    this.targetInner.style.display = '';

    if (this._closeInstances.length) {
      this._closeInstances.forEach(o => {
        if (o) o.show && o.close();
      });

      setTimeout(() => this.setOpenParameters(), this.duration);
    } else {
      this.setOpenParameters();
    }
  };

  close = () => {
    if (this.isAnimating) return;
    if (this.show) this.isAnimating = true;
    this.show = false;
    this.setProps();

    this.toggleStart && this.toggleStart(this.target, 'hide', this.show);
  };

  toggle() {
    const preventClosing = this.preventAllClose && this.preventLastClose();
    if (!preventClosing) {
      this.show ? this.close() : this.open();
    }
  }

  destroy = () => {
    this.handler.classList.remove(this.openClassName);
    this.handler.setAttribute('aria-expanded', 'false');
    this.target.removeAttribute('style');
    this.target.setAttribute('aria-hidden', 'false');
    this.target.classList.remove(this.openClassName);

    this.target.removeEventListener(
      transitionEndEvent,
      this.transitionEndShower,
      false
    );
    this.handler.removeEventListener('click', this.toggleAccordion);
  };

  init = () => {
    if (!this.target || !this.targetInner || !this.handler) {
      console.error(
        `Pass valid elements to shower class. Handler is ${
          this.handler ? `#${this.handler.id}` : this.handler
        }, body is ${
          this.target ? `#${this.target.id}` : this.target
        }, body inner is ${
          this.targetInner ? `#${this.targetInner.id}` : this.targetInner
        }`
      );
      return;
    }
    this.setInitialProps();
    this.setProps();
    this.target.addEventListener(
      transitionEndEvent,
      this.transitionEndShower,
      false
    );
    this.handler.addEventListener('click', this.toggleAccordion);
  };
}

export { Accordion };
